/**
 * EXECUTOR-WRITE-1 Build 2 Phase 3 (s143) — the shared executor edit primitive.
 *
 * Wraps the shared EditableCell (G7 — one type-aware input surface for planner + executor) with
 * an explicit save flow + an optional provenance note, and PATCHes the CANONICAL field via
 * /api/executor/:clientId/entry (which writes generic_entries + appends an executor_field_changes
 * row). On save it invalidates the task's /bindings query so the drawer re-renders the corrected
 * value + the "edited · was …" badge. Mounted per record cell in RecordRow; reusable by a future
 * plan-view surface that renders the same ResolvedCell shape.
 *
 * Cat A cells (secureFilePointer) are never editable and never reach this component.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { EditableCell } from "@/components/EditableCell";
import { apiRequest } from "@/lib/queryClient";
import type { ResolvedCell } from "@shared/task-bindings";
import { SAGE, WARM } from "@shared/brand";


// Map the generic field type → EditableCell's supported input type. Types EditableCell doesn't
// take a value-string for cleanly here (address/select) fall back to a plain text input.
function editableCellType(ft?: string): "text" | "currency" | "date" | "email" | "number" | "tel" | "yes_no" {
  switch (ft) {
    case "currency": return "currency";
    case "date": return "date";
    case "email": return "email";
    case "number": return "number";
    case "phone": return "tel";
    case "yes_no": return "yes_no";
    default: return "text";
  }
}

export function EditableRecordField({
  clientId, taskKey, section, rowId, cell, onDone, onSaved,
}: {
  clientId: string;
  /** drawer surface: the task whose /bindings query is invalidated on save. Omit off the drawer
   *  (e.g. the plan view) and pass `onSaved` to refresh that surface's own queries instead. */
  taskKey?: string;
  /** the section SLUG (from the Class-A binding) */
  section: string;
  rowId: string;
  cell: ResolvedCell;
  onDone: () => void;
  /** called after a successful save (before onDone) so a non-drawer host can invalidate its own
   *  queries. The drawer relies on `taskKey` invalidation and needs neither. */
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(cell.value ?? "");
  const [note, setNote] = useState("");

  const save = useMutation({
    mutationFn: async () =>
      (await apiRequest("PATCH", `/api/executor/${clientId}/entry`, {
        section,
        rowId,
        fieldId: cell.fieldId,
        fieldKey: cell.fieldKey,
        newValue: draft,
        note: note.trim() || undefined,
      })).json(),
    onSuccess: () => {
      if (taskKey) qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks/${taskKey}/bindings`] });
      onSaved?.();
      onDone();
    },
  });

  return (
    <div className="rounded-md p-2 mt-1" style={{ border: `1px solid ${WARM.stone}` }} data-testid={`edit-field-${cell.fieldKey}`}>
      <div className="text-[11px] mb-1" style={{ color: WARM.taupe }}>{cell.fieldLabel ?? cell.label}</div>
      <EditableCell
        value={draft}
        onChange={setDraft}
        onBlur={() => {}}
        type={editableCellType(cell.fieldType)}
        multiline={cell.fieldType === "multiline"}
        showBorder
        ariaLabel={cell.label}
        testId={`edit-input-${cell.fieldKey}`}
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why are you correcting this? (optional)"
        className="w-full mt-1.5 rounded-md px-2 py-1 text-[12px] outline-none"
        style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
        data-testid={`edit-note-${cell.fieldKey}`}
      />
      <div className="flex gap-2 mt-1.5">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md px-3 py-1 text-[12px] font-semibold text-white inline-flex items-center gap-1"
          style={{ backgroundColor: SAGE[600] }}
          data-testid={`edit-save-${cell.fieldKey}`}
        >
          {save.isPending && <Loader2 className="h-3 w-3 animate-spin" />}Save
        </button>
        <button type="button" onClick={onDone} className="text-[12px]" style={{ color: WARM.taupe }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
